/**
 * Preset registry of the Threshold Segmentation operation
 * (SR-WOR-H-27-2). Presets are DATA, not code forks — each tab of the
 * panel is one entry here (pattern: {@link STRUCTURE_CODES}), and adding
 * a preset must never require a new pipeline branch. The Body preset's
 * clinical specifics remain SR-WOR-H-27-1.
 */
import { DicomColor } from '~/domain/dicom/dicomColor';
import {
    DicomStructureSet,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { STRUCTURE_CODES } from '~/domain/dicom/structureCodes';
import {
    HouseWindowingPreset,
    requireWindowingPreset,
} from '~/domain/patient/windowLevel';
import {
    BODY_CONTOUR_CT_THRESHOLD_HU,
    BODY_CONTOUR_DEFAULT_KEEP_2D_COUNT,
    BODY_CONTOUR_DEFAULT_KEEP_3D_COUNT,
    BODY_CONTOUR_MIN_PART_AREA_MM2,
    BODY_CONTOUR_THRESHOLD_MAX_HU,
    BODY_CONTOUR_THRESHOLD_MIN_HU,
} from '~/engine/util/algorithms/sdf/BodyContourExtraction';

export type ThresholdSegmentationPresetId = 'body' | 'bone' | 'highDensity';

/** An optional stage the panel may expose as a toggle. When `exposed` is
 *  false the stage is locked to `defaultEnabled` (e.g. the Body preset's
 *  mandatory cavity fill). */
export interface PresetStageConfig {
    exposed: boolean;
    defaultEnabled: boolean;
}

/** Effective enablement of an optional stage: a stage the preset does not
 *  expose is locked to its preset default, never to tab state the user has
 *  no way to see or change. The single rule every `PresetStageConfig`
 *  consumer must route through — a stage that reads its tab flag directly
 *  would silently run (or not) against the preset author's declared
 *  intent. */
export function stageEnabled(
    stage: PresetStageConfig,
    tabValue: boolean
): boolean {
    return stage.exposed ? tabValue : stage.defaultEnabled;
}

/**
 * Upper bound of a preset's threshold control: a fixed HU value, or
 * `'imageMax'` to derive it from the ACTIVE IMAGE's maximum intensity.
 * A preset that segments the very top of the intensity range (High
 * density) cannot use a fixed bound: a scan whose metal artifacts peak
 * above it would have them unreachable, and one that peaks below it
 * would offer dead slider travel where every position yields an empty
 * result.
 */
export type ThresholdUpperBound = number | 'imageMax';

/**
 * A preset's optional INCLUSIVE upper HU bound — the second handle of a
 * two-handle threshold control. Absent (Body, High density) the preset
 * classifies at-or-above and unbounded, exactly as before; present
 * (Bone) a voxel belongs to the structure iff
 * `lower ≤ HU ≤ upper`, both inclusive.
 *
 * Bone bounds its band for a clinical reason: at 150 HU with no ceiling
 * a "Bones" structure also captures metal implants, fillings, surgical
 * clips and contrast. The band is NOT clamped against the image's own
 * maximum — an upper bound above it is harmless (it includes everything
 * present), where clamping would rewrite a clinical value the user set.
 */
export interface ThresholdBandUpper {
    defaultHu: number;
    minHu: number;
    maxHu: number;
}

/**
 * Effective threshold control range for a preset on a given image, or
 * the reason the preset cannot operate on it.
 */
export type ThresholdRange =
    | { kind: 'range'; minHu: number; maxHu: number }
    | { kind: 'unavailable'; imageMaxHu: number; defaultHu: number };

/**
 * Resolve the threshold control range against the active image. This is
 * the LOWER bound's range and the availability verdict only: a banded
 * preset's upper bound is a fixed registry range that is deliberately
 * NOT resolved against the image (see {@link ThresholdBandUpper}), so
 * the logic below is unchanged by banding. An
 * image whose peak intensity does not reach the preset's default
 * threshold has nothing for that preset to segment — reported as
 * `unavailable` so the panel can surface the reason and refuse, never
 * clamped onto a degenerate slider (a silently clamped clinical
 * threshold is exactly the wrong-result-presented-as-valid hazard).
 *
 * Unavailability keys on the preset's DEFAULT threshold, not on its
 * MINIMUM — deliberately, now that High density's minimum (1000 HU)
 * sits below its default (2000 HU). Keying on the minimum would leave
 * the panel open on an image whose maximum lies between the two, where
 * the control still opens AT the default: the slider would show a value
 * above its own maximum, i.e. a clinical threshold no voxel reaches
 * presented as the operating point, and only silently clamping that
 * value (forbidden — never "fix" a clinical value without surfacing it)
 * could make it consistent again. Keying on the default is the
 * conservative reading: the preset is offered only on images it can
 * actually operate on at its declared operating point, and the lowered
 * minimum only widens how far DOWN the user may dial on those images.
 *
 * @param imageMaxHu The image's maximum voxel intensity, in HU.
 */
export function resolveThresholdRange(
    preset: ThresholdSegmentationPreset,
    imageMaxHu: number
): ThresholdRange {
    const { minHu, maxHu, defaultHu } = preset.threshold;
    if (maxHu !== 'imageMax') {
        return { kind: 'range', minHu, maxHu };
    }
    // Whole HU: the control steps in 1 HU, and rounding the bound UP
    // would offer a threshold no voxel in the image reaches.
    const imageMax = Math.floor(imageMaxHu);
    if (imageMax <= defaultHu) {
        return { kind: 'unavailable', imageMaxHu: imageMax, defaultHu };
    }
    return { kind: 'range', minHu, maxHu: imageMax };
}

export interface ThresholdSegmentationPreset {
    id: ThresholdSegmentationPresetId;
    /** i18n keys for the tab label and the panel description. */
    tabLabelId: string;
    descriptionId: string;
    /**
     * House windowing preset a review shortcut on the tab applies —
     * through the SAME transient event path as the viewport preset menu,
     * so it is exactly as reversible as a manual window/level change.
     * Absent for presets whose result needs no special display window
     * (Body: the default window already shows the outline).
     */
    reviewWindowLevel?: HouseWindowingPreset;
    threshold: {
        defaultHu: number;
        minHu: number;
        /** Upper bound of the LOWER handle's adjustable travel — see
         *  {@link ThresholdUpperBound} and {@link resolveThresholdRange}.
         *  Never an inclusion bound: a single-sided preset includes
         *  everything at or above the threshold, however high. */
        maxHu: ThresholdUpperBound;
        /**
         * Optional inclusive UPPER inclusion bound, making the preset
         * BANDED — see {@link ThresholdBandUpper}. Absent leaves the
         * preset single-sided and unbounded above (Body, High density),
         * so adding a preset needs no change here.
         */
        upper?: ThresholdBandUpper;
    };
    /**
     * Minimum in-plane part area, in mm² — the DOMAIN unit, because these
     * values feed the engine directly. The panel displays and accepts cm²
     * and converts at the UI boundary (see docs/ui_specs.md).
     */
    minPartSize: PresetStageConfig & {
        defaultAreaMm2: number;
        minAreaMm2: number;
    };
    fillHoles2d: PresetStageConfig;
    /**
     * Gaussian smoothing. `defaultStrength` is a CONTINUOUS strength
     * position on the 1..MAX_SMOOTHING_LEVEL scale (0.1-quantized by the
     * panel's slider), not a level index: an integer default therefore
     * still maps to exactly its historical sigma, while the user may
     * settle anywhere between two of them.
     */
    smoothing: PresetStageConfig & { defaultStrength: number };
    keepLargest2d: PresetStageConfig & { defaultCount: number };
    keepLargest3d: PresetStageConfig & { defaultCount: number };
    /**
     * Final per-slice cavity re-fill after the cleanup stages: smoothing
     * and cleanup can reopen cavities the mask-phase fill closed. Locked
     * (never exposed): Body on — an EXTERNAL with reopened holes is not
     * a valid outline; Bone off — a re-fill would swallow the spinal
     * canal; High density off.
     */
    finalFillHoles2d: PresetStageConfig;
    /** Symmetric margin (mm), after the cleanup stages. Exposed only for
     *  High density; 0 disables the stage. */
    margin: {
        exposed: boolean;
        defaultMm: number;
        minMm: number;
        maxMm: number;
    };
    /**
     * Intersect the result with the structure set's EXTERNAL structure
     * (applied after the margin, before the restriction clamp), so
     * couch / cables / air artifacts outside the patient outline never
     * survive. IMPLICIT — never exposed as a control: enabled for Bone /
     * High density, where it simply runs whenever a usable EXTERNAL
     * exists and is inert when none does; the Body preset generates the
     * EXTERNAL itself and never applies it.
     */
    keepInsideExternal: PresetStageConfig;
    /**
     * Whether a displayed preview recedes the OTHER structures in the
     * viewport. The recede is not a fade — the contour shader folds the
     * alpha into RGB — so it RE-TINTS every other structure for as long
     * as the preview is up (a skin-tone EXTERNAL renders dark brown).
     *
     * True only for Body, whose preview is drawn exactly on top of the
     * EXTERNAL it replaces, in that structure's own color: without the
     * recede the two outlines are indistinguishable. Bone / High density
     * produce a DIFFERENT structure and are clipped to the EXTERNAL,
     * which the user reads as the reference the result sits inside — it
     * must keep its own color.
     */
    previewDimsOtherStructures: boolean;
    /**
     * Whether a completed preview additionally reports how many 3D
     * connected components it found. Every preset reports the previewed
     * structure's VOLUME — that is what makes a visually sparse preview
     * confirmable at all — but the component COUNT is only meaningful
     * where scattered separate pieces are the expected output: High
     * density segments implants, markers, fillings and clips, which
     * genuinely are N detached objects, and "3 components" is how the
     * user checks that the threshold caught the ones they meant.
     *
     * Off elsewhere and deliberately so: Body and Bone deliver one
     * outline (or one skeleton) and a count would be noise — and the
     * count costs a connected-component labeling pass over the final
     * field, which no preset should pay for a number it does not show.
     */
    reportComponents?: boolean;
    output: {
        /** Default structure name for the create / overwrite-own path. */
        name: string;
        interpretedType: RoiInterpretedType;
        identificationCode?: RoiIdentificationCodeSequence;
        color: DicomColor;
        /**
         * 'single-external': the structure set's one EXTERNAL is the
         * fixed output (Body). 'overwrite-own-by-name': the output is
         * resolved by name — an unapproved same-name structure is
         * overwritten (re-apply replaces what this tool created), an
         * approved one refuses, otherwise a new structure is created.
         */
        mode: 'single-external' | 'overwrite-own-by-name';
    };
}

/** Tab order: Body first — it is the historical one-click entry point. */
export const THRESHOLD_SEGMENTATION_PRESET_ORDER: ThresholdSegmentationPresetId[] =
    ['body', 'bone', 'highDensity'];

export const THRESHOLD_SEGMENTATION_PRESETS: Record<
    ThresholdSegmentationPresetId,
    ThresholdSegmentationPreset
> = {
    // Exactly the established body-contour behavior (SR-WOR-H-27-1),
    // except min-size now defaults ON — clinical review value.
    body: {
        id: 'body',
        tabLabelId: 'thresholdSeg.tab.body',
        descriptionId: 'thresholdSeg.preset.body.description',
        threshold: {
            defaultHu: BODY_CONTOUR_CT_THRESHOLD_HU,
            minHu: BODY_CONTOUR_THRESHOLD_MIN_HU,
            maxHu: BODY_CONTOUR_THRESHOLD_MAX_HU,
        },
        minPartSize: {
            exposed: true,
            defaultEnabled: true,
            defaultAreaMm2: BODY_CONTOUR_MIN_PART_AREA_MM2,
            minAreaMm2: 10,
        },
        // Locked ON: an EXTERNAL with unfilled lung/bowel air is never a
        // valid patient outline.
        fillHoles2d: { exposed: false, defaultEnabled: true },
        smoothing: { exposed: true, defaultEnabled: true, defaultStrength: 6 },
        keepLargest2d: {
            exposed: true,
            defaultEnabled: true,
            defaultCount: BODY_CONTOUR_DEFAULT_KEEP_2D_COUNT,
        },
        keepLargest3d: {
            exposed: true,
            defaultEnabled: true,
            defaultCount: BODY_CONTOUR_DEFAULT_KEEP_3D_COUNT,
        },
        // Locked ON (mirror of the mask-phase fill): smoothing/cleanup
        // can reopen enclosed cavities, and an EXTERNAL with holes is
        // never a valid outline.
        finalFillHoles2d: { exposed: false, defaultEnabled: true },
        margin: { exposed: false, defaultMm: 0, minMm: 0, maxMm: 5 },
        // The Body preset IS the EXTERNAL generator — clipping to the
        // structure it replaces would be circular.
        keepInsideExternal: { exposed: false, defaultEnabled: false },
        // The preview lands on top of the EXTERNAL it replaces, in that
        // structure's own color — without the recede the outgoing and
        // incoming outlines cannot be told apart.
        previewDimsOtherStructures: true,
        output: {
            name: 'Body',
            interpretedType: RoiInterpretedType.EXTERNAL,
            identificationCode: STRUCTURE_CODES.body,
            color: { R: 231, G: 179, B: 143 },
            mode: 'single-external',
        },
    },
    bone: {
        id: 'bone',
        tabLabelId: 'thresholdSeg.tab.bone',
        descriptionId: 'thresholdSeg.preset.bone.description',
        // The house Bone window (1600 / 500) — inside the clinical
        // bone-window envelope, and reusing the named entry means the
        // viewport preset dropdown honestly shows "Bone" as active
        // after the shortcut.
        reviewWindowLevel: requireWindowingPreset('Bone'),
        // The one BANDED preset: bone lives between ~150 HU and cortical
        // density, so an unbounded at-or-above threshold also captured
        // metal implants, fillings, clips and contrast. The ceiling
        // defaults to 2000 HU — the High density preset's own default, so
        // the two partition rather than overlap — and reaches 3000 HU,
        // the Dental review window's ceiling. It is never clamped against
        // the image maximum: a ceiling above it simply includes
        // everything present (see {@link ThresholdBandUpper}).
        threshold: {
            defaultHu: 150,
            minHu: 100,
            maxHu: 1000,
            upper: { defaultHu: 2000, minHu: 1000, maxHu: 3000 },
        },
        minPartSize: {
            exposed: true,
            defaultEnabled: true,
            defaultAreaMm2: 50,
            minAreaMm2: 10,
        },
        // Default OFF for a clinical reason: the vertebral ring encloses
        // the spinal canal in-plane, so a 2D fill would swallow it into
        // the bone structure.
        fillHoles2d: { exposed: true, defaultEnabled: false },
        smoothing: { exposed: true, defaultEnabled: true, defaultStrength: 3 },
        keepLargest2d: {
            exposed: false,
            defaultEnabled: false,
            defaultCount: 1,
        },
        keepLargest3d: {
            exposed: true,
            defaultEnabled: false,
            defaultCount: 1,
        },
        // Locked OFF, same spinal-canal rationale as the mask-phase fill.
        finalFillHoles2d: { exposed: false, defaultEnabled: false },
        margin: { exposed: false, defaultMm: 0, minMm: 0, maxMm: 5 },
        // Implicit (never exposed): runs whenever a usable EXTERNAL
        // exists, inert when none does.
        keepInsideExternal: { exposed: false, defaultEnabled: true },
        // The result is a separate structure clipped INSIDE the
        // EXTERNAL: re-tinting the patient outline (and every other
        // structure) while the preview is up is the display defect, not
        // an affordance.
        previewDimsOtherStructures: false,
        output: {
            name: 'Bones',
            interpretedType: RoiInterpretedType.ORGAN,
            // Dark blue — legible against the grayscale CT on the dark
            // viewport, where a light blue washed out.
            color: { R: 40, G: 90, B: 180 },
            mode: 'overwrite-own-by-name',
        },
    },
    highDensity: {
        id: 'highDensity',
        tabLabelId: 'thresholdSeg.tab.highDensity',
        descriptionId: 'thresholdSeg.preset.highDensity.description',
        // The house wide/high window (Dental, 3000 / 900): its display
        // ramp (-600..2400 HU) keeps the preset's default threshold
        // (2000 HU) on visible grayscale, close to the conventional
        // metal window. NOT 'Metal' (1 / 2800) — that is a binary
        // threshold view at the wrong HU.
        reviewWindowLevel: requireWindowingPreset('Dental'),
        // Ceiling is the image's own maximum: implant/marker densities
        // vary by material and scanner, and a fixed ceiling would either
        // truncate the usable range or offer thresholds no voxel reaches.
        // The floor sits BELOW the default (1000 vs 2000 HU): dense bone
        // and low-density implants (some markers, cement) live in that
        // band, so the user must be able to dial down to reach them.
        // Availability still keys on the DEFAULT — see
        // {@link resolveThresholdRange}.
        threshold: { defaultHu: 2000, minHu: 1000, maxHu: 'imageMax' },
        minPartSize: {
            exposed: true,
            defaultEnabled: false,
            defaultAreaMm2: 10,
            minAreaMm2: 10,
        },
        fillHoles2d: { exposed: true, defaultEnabled: false },
        smoothing: { exposed: true, defaultEnabled: false, defaultStrength: 2 },
        keepLargest2d: {
            exposed: false,
            defaultEnabled: false,
            defaultCount: 1,
        },
        keepLargest3d: {
            exposed: true,
            defaultEnabled: false,
            defaultCount: 1,
        },
        finalFillHoles2d: { exposed: false, defaultEnabled: false },
        margin: { exposed: true, defaultMm: 1.0, minMm: 0, maxMm: 5 },
        // Implicit (never exposed): runs whenever a usable EXTERNAL
        // exists, inert when none does.
        keepInsideExternal: { exposed: false, defaultEnabled: true },
        // Same as Bone: the EXTERNAL is the reference the implants sit
        // inside and keeps its own color.
        previewDimsOtherStructures: false,
        // The one preset whose normal result is a handful of separate
        // objects — see {@link ThresholdSegmentationPreset.reportComponents}.
        reportComponents: true,
        output: {
            name: 'HighDensity',
            interpretedType: RoiInterpretedType.AVOIDANCE,
            color: { R: 90, G: 90, B: 95 },
            mode: 'overwrite-own-by-name',
        },
    },
};

/** Minimal ROI facts the by-name output resolution needs. */
export interface OwnOutputCandidate {
    roiNumber: number;
    name: string;
    isApproved: boolean;
}

export type OwnOutputResolution =
    | { kind: 'create' }
    | { kind: 'overwrite'; roiNumber: number; name: string }
    | { kind: 'refuse-approved'; name: string }
    | { kind: 'refuse-ambiguous'; name: string };

/**
 * Resolve the overwrite-own-by-name output. An unapproved structure with
 * the requested name is overwritten, so re-apply replaces the structure
 * this tool created earlier. An approved one refuses, because approval
 * protection beats convenience. No match creates a new structure. Several
 * matches refuse: DICOM makes only ROI numbers unique, and the name does
 * not say which structure to replace.
 */
export function resolveOwnOutputByName(
    rois: readonly OwnOutputCandidate[],
    name: string
): OwnOutputResolution {
    // Domain name equality (trimmed, case-insensitive): 'bones' and
    // 'Bones' are the same structure to DICOM-name-based consumers.
    const matches = rois.filter((r) =>
        DicomStructureSet.structureNamesEqual(r.name, name)
    );
    const [match] = matches;
    if (!match) {
        return { kind: 'create' };
    }
    if (matches.length > 1) {
        return { kind: 'refuse-ambiguous', name: name.trim() };
    }
    if (match.isApproved) {
        return { kind: 'refuse-approved', name: match.name };
    }
    // Carry the STORED name (not the typed one): the "Applying replaces…"
    // banner and the overwrite target must agree on the same resolution.
    return { kind: 'overwrite', roiNumber: match.roiNumber, name: match.name };
}
